import { getAuthToken, handleApiError } from './apiUtils';
import { Vehicle, CreateVehicleData, UpdateVehicleData, VehicleResponse, Branch } from '../store/slices/vehicleSlice';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api/v1';

export interface VehicleListParams {
    page?: number;
    per_page?: number;
    query?: string;
    branch_id?: number;
    type_of_vehicle?: string;
    ownership?: string;
    status?: string;
}

export interface BackendResponse<T> {
    success: boolean;
    message: string;
    data: T;
}

export const vehicleApi = {
    // Get vehicles list with filtering and pagination
    getVehicles: async (params?: VehicleListParams): Promise<VehicleResponse> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const queryParams = new URLSearchParams();
        Object.entries(params || {}).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value.toString());
            }
        });

        const response = await fetch(`${API_BASE_URL}/vehicles?${queryParams}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<VehicleResponse> = await response.json();
        return data.data;
    },

    // Get single vehicle details
    getVehicle: async (id: number): Promise<{ vehicle: Vehicle }> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/vehicles/${id}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ vehicle: Vehicle }> = await response.json();
        return data.data;
    },

    // Create new vehicle
    createVehicle: async (vehicleData: CreateVehicleData): Promise<{ vehicle: Vehicle }> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/vehicles`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(vehicleData),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ vehicle: Vehicle }> = await response.json();
        return data.data;
    },

    // Update vehicle
    updateVehicle: async (id: number, vehicleData: UpdateVehicleData): Promise<{ vehicle: Vehicle }> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/vehicles/${id}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(vehicleData),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ vehicle: Vehicle }> = await response.json();
        return data.data;
    },

    // Delete vehicle
    deleteVehicle: async (id: number): Promise<void> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/vehicles/${id}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }
    },

    // Get branches for dropdown
    getBranches: async (): Promise<Branch[]> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/vehicles/branches`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<Branch[]> = await response.json();
        return data.data;
    },
};
